import React, { useEffect, useState } from "react";
import FamilyOverview from "../../family/FamilyOverview";
import ChildProfileView from "../../family/ChildProfileView";
import ChildPersonalEdit from "../../family/ChildPersonalEdit";
import ChildPrivacyEdit from "../../family/ChildPrivacyEdit";
import ChildHealthView from "../../family/ChildHealthView";
import SavedView from "../../family/components/SavedView";
import { getChildKey } from "../../family/utils/childUtils";

const FamilyTab = ({
  t,
  family,
  familyDraft,
  setFamilyDraft,
  familyEditMode,
  setFamilyEditMode,
  hasPendingFamilyUpdate,
  hasPendingFamilySituationRequest,
  handleSaveFamilySituation,
  onSubmitFamilySituationRequest,
  onSubmitTeacherContactRequest,
  parentData,
  onOpenParent,
  onOpenChild,
  onAddChild,
  classroomsByChild = {},
  privacyByChild = {},
  privacyActionChildId,
  onPrivacyActionHandled,
  bsnActionChildId,
  onBsnActionHandled,
  healthActionChildId,
  onHealthActionHandled,
  loadPrivacyFor,
  loadClassroomsForChild,
  startChildInfoEdit,
  childInfoEdit = {},
  updateChildInfoDraft,
  cancelChildInfoEdit,
  submitChildInfoEdit,
  savePrivacyFor,
  pendingChildren = [],
  pendingChildUpdateSet = new Set(),
}) => {
  const [view, setView] = useState("overview");
  const [selectedChild, setSelectedChild] = useState(null);

  const children = Array.isArray(family?.children) ? family.children : [];
  const selectedKey = getChildKey(selectedChild);
  const liveSelectedChild =
    children.find((child) => getChildKey(child) === selectedKey) || selectedChild;

  useEffect(() => {
    if (!privacyActionChildId) return;

    const child = children.find((item) => getChildKey(item) === String(privacyActionChildId));
    if (!child) return;

    setSelectedChild(child);
    setView("edit-privacy");
    loadPrivacyFor?.(child.id);
    loadClassroomsForChild?.(child.id);
    onPrivacyActionHandled?.();
  }, [children, loadClassroomsForChild, loadPrivacyFor, onPrivacyActionHandled, privacyActionChildId]);

  useEffect(() => {
    if (!bsnActionChildId) return;

    const child = children.find((item) => getChildKey(item) === String(bsnActionChildId));
    if (!child) return;

    setSelectedChild(child);
    setView("edit-personal");
    startChildInfoEdit?.(child);
    loadPrivacyFor?.(child.id);
    loadClassroomsForChild?.(child.id);
    onBsnActionHandled?.();
  }, [bsnActionChildId, children, loadClassroomsForChild, loadPrivacyFor, onBsnActionHandled, startChildInfoEdit]);

  useEffect(() => {
    if (!healthActionChildId) return;

    const child = children.find((item) => getChildKey(item) === String(healthActionChildId));
    if (!child) return;

    setSelectedChild(child);
    setView("health");
    loadClassroomsForChild?.(child.id);
    onHealthActionHandled?.();
  }, [children, healthActionChildId, loadClassroomsForChild, onHealthActionHandled]);

  const openChildProfile = (child) => {
    setSelectedChild(child);
    setView("profile");
    loadPrivacyFor?.(child.id);
    loadClassroomsForChild?.(child.id);
  };

  const backToOverview = () => {
    setView("overview");
    setSelectedChild(null);
  };

  if (view === "profile" && liveSelectedChild) {
    return (
      <ChildProfileView
        t={t}
        child={liveSelectedChild}
        classroomsByChild={classroomsByChild}
        privacyByChild={privacyByChild}
        pendingChildren={pendingChildren}
        pendingChildUpdateSet={pendingChildUpdateSet}
        onBack={backToOverview}
        onEditPersonal={() => setView("edit-personal")}
        onEditPrivacy={() => setView("edit-privacy")}
        onOpenHealth={() => setView("health")}
      />
    );
  }

  if (view === "edit-personal" && liveSelectedChild) {
    return (
      <ChildPersonalEdit
        t={t}
        child={liveSelectedChild}
        classroomsByChild={classroomsByChild}
        childInfoEdit={childInfoEdit}
        startChildInfoEdit={startChildInfoEdit}
        updateChildInfoDraft={updateChildInfoDraft}
        cancelChildInfoEdit={cancelChildInfoEdit}
        submitChildInfoEdit={submitChildInfoEdit}
        pendingChildren={pendingChildren}
        pendingChildUpdateSet={pendingChildUpdateSet}
        onBack={() => setView("profile")}
        onSaved={() => setView("saved")}
      />
    );
  }

  if (view === "edit-privacy" && liveSelectedChild) {
    return (
      <ChildPrivacyEdit
        t={t}
        child={liveSelectedChild}
        classroomsByChild={classroomsByChild}
        privacyByChild={privacyByChild}
        savePrivacyFor={savePrivacyFor}
        onBack={() => setView("profile")}
        onSaved={() => setView("saved")}
      />
    );
  }

  if (view === "health" && liveSelectedChild) {
    return (
      <ChildHealthView
        t={t}
        child={liveSelectedChild}
        classroomsByChild={classroomsByChild}
        pendingChildren={pendingChildren}
        pendingChildUpdateSet={pendingChildUpdateSet}
        onBack={() => setView("profile")}
      />
    );
  }

  if (view === "saved") {
    return <SavedView t={t} onBackToProfile={() => setView("profile")} />;
  }

  return (
    <FamilyOverview
      t={t}
      family={family}
      familyDraft={familyDraft}
      setFamilyDraft={setFamilyDraft}
      familyEditMode={familyEditMode}
      setFamilyEditMode={setFamilyEditMode}
      hasPendingFamilyUpdate={hasPendingFamilyUpdate}
      hasPendingFamilySituationRequest={hasPendingFamilySituationRequest}
      handleSaveFamilySituation={handleSaveFamilySituation}
      onSubmitFamilySituationRequest={onSubmitFamilySituationRequest}
      parentData={parentData}
      onOpenParent={onOpenParent}
      onSelectChild={openChildProfile}
      onAddChild={onAddChild || onOpenChild}
      classroomsByChild={classroomsByChild}
      privacyByChild={privacyByChild}
    />
  );
};

export default FamilyTab;
